import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtPct } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { X, Plus } from "lucide-react";
import { toast } from "sonner";
import { usePriceTicks } from "../context/LiveStreamContext";
import { StalenessBadge } from "../components/StalenessBadge";

export default function Watchlist() {
  const [items, setItems] = useState([]);
  const [addSym, setAddSym] = useState("");
  const [addType, setAddType] = useState("stock");
  const [adding, setAdding] = useState(false);
  const navigate = useNavigate();
  const live = usePriceTicks();
  // Track which cell just changed (for flash animation)
  const [flash, setFlash] = useState({});

  const load = () => api.get("/watchlist").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const addSymbol = async (e) => {
    e && e.preventDefault();
    const sym = addSym.trim().toUpperCase().replace(/\s+/g, "");
    if (!sym) return;
    if (!/^[A-Z0-9.\-]{1,12}$/.test(sym)) {
      toast.error("Ticker must be 1-12 letters/numbers (e.g. AAPL, BTC, BRK.B)");
      return;
    }
    setAdding(true);
    try {
      // Try to verify — but DON'T block adding if the upstream data provider
      // is temporarily unavailable. The watchlist enricher will fetch a price
      // on the next refresh; the row shows "no data" until then.
      let verified = null;
      try {
        const { data } = await api.get(`/assets/${addType}/${sym}`);
        if (data && (data.price != null || data.name)) verified = data;
      } catch { /* ignore — we'll add anyway */ }
      await api.post("/watchlist", { symbol: sym, asset_type: addType });
      if (verified) {
        toast.success(`Added ${sym}`, {
          description: verified.name ? `${verified.name} · ${fmtMoney(verified.price)}` : undefined,
        });
      } else {
        toast.success(`Added ${sym}`, {
          description: "Price will populate on the next refresh.",
        });
      }
      setAddSym("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add symbol");
    }
    setAdding(false);
  };

  // Merge live ticks into rows (immutable, memoized)
  const rows = useMemo(() => items.map((it) => {
    const tick = live[it.symbol];
    if (!tick) return it;
    return { ...it, price: tick.p, change_pct: tick.ch, _live: true };
  }), [items, live]);

  // Trigger flash whenever a tick's price differs from previous tick
  useEffect(() => {
    const changed = {};
    for (const [sym, info] of Object.entries(live)) {
      if (info.prev !== undefined && info.p !== info.prev) {
        changed[sym] = info.p > info.prev ? "up" : "down";
      }
    }
    if (Object.keys(changed).length) {
      setFlash((f) => ({ ...f, ...changed }));
      setTimeout(() => setFlash((f) => {
        const n = { ...f };
        for (const s of Object.keys(changed)) delete n[s];
        return n;
      }), 900);
    }
  }, [live]);

  const remove = async (e, symbol) => {
    e.stopPropagation();
    await api.delete(`/watchlist/${symbol}`);
    toast.success("Removed");
    load();
  };

  return (
    <div className="space-y-6" data-testid="watchlist-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Saved</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Watchlist</h1>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2" data-testid="watchlist-live-hint">
          <StalenessBadge testId="watchlist-freshness" />
        </div>
      </div>

      {/* Add any ticker */}
      <Card className="p-4 rounded-xl border-border" data-testid="watchlist-add-card">
        <form onSubmit={addSymbol} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <div className="flex gap-1 sm:min-w-[220px]">
            <button type="button"
                    onClick={() => setAddType("stock")}
                    data-testid="watchlist-type-stock"
                    className={`flex-1 px-3 py-1.5 rounded-full text-xs font-mono-tab uppercase tracking-wider transition-colors ${
                      addType === "stock"
                        ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                        : "bg-secondary/30 text-muted-foreground border border-transparent hover:text-foreground"
                    }`}>
              Stock
            </button>
            <button type="button"
                    onClick={() => setAddType("crypto")}
                    data-testid="watchlist-type-crypto"
                    className={`flex-1 px-3 py-1.5 rounded-full text-xs font-mono-tab uppercase tracking-wider transition-colors ${
                      addType === "crypto"
                        ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                        : "bg-secondary/30 text-muted-foreground border border-transparent hover:text-foreground"
                    }`}>
              Crypto
            </button>
          </div>
          <Input
            value={addSym}
            onChange={(e) => setAddSym(e.target.value.toUpperCase())}
            placeholder={addType === "stock" ? "Type any ticker (e.g. SHAZ, TSLA, BRK.B)" : "Type any coin ID (e.g. bitcoin, ethereum, solana)"}
            data-testid="watchlist-add-input"
            maxLength={12}
            autoComplete="off"
            className="flex-1 font-mono-tab uppercase"
          />
          <Button type="submit" disabled={adding || !addSym.trim()}
                  data-testid="watchlist-add-btn"
                  className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold px-5">
            <Plus className="w-4 h-4 mr-1" /> {adding ? "Verifying…" : "Add"}
          </Button>
        </form>
        <div className="text-[11px] text-muted-foreground mt-2">
          Type any US-listed ticker or supported crypto. Aanar verifies the symbol resolves to a real price before adding it.
        </div>
      </Card>

      {rows.length ? (
        <Card className="p-2 rounded-xl border-border">
          <div className="hidden sm:grid grid-cols-12 text-xs uppercase tracking-wider text-muted-foreground px-4 py-3">
            <div className="col-span-4">Asset</div>
            <div className="col-span-3 text-right">Price</div>
            <div className="col-span-3 text-right">24h</div>
            <div className="col-span-2 text-right">Action</div>
          </div>
          <div className="divide-y divide-border">
            {rows.map((it) => (
              <div key={it.symbol}
                   onClick={() => navigate(`/asset/${it.asset_type}/${it.symbol}`)}
                   data-testid={`watch-row-${it.symbol}`}
                   className="grid grid-cols-2 sm:grid-cols-12 gap-y-1 items-center px-4 py-3 hover:bg-secondary/40 cursor-pointer">
                <div className="sm:col-span-4">
                  <div className="font-medium">{it.ticker || it.symbol}</div>
                  <div className="text-xs text-muted-foreground">{it.name || it.symbol}</div>
                </div>
                <div className={`sm:col-span-3 text-right font-mono-tab transition-colors duration-500 ${
                  flash[it.symbol] === "up" ? "text-[hsl(var(--gain))]" :
                  flash[it.symbol] === "down" ? "text-[hsl(var(--loss))]" : ""
                }`} data-testid={`watch-price-${it.symbol}`}>
                  {it.price != null ? fmtMoney(it.price, it.price > 100 ? 2 : 4) : <span className="text-muted-foreground italic text-xs">no data</span>}
                </div>
                <div className={`col-start-2 row-start-1 sm:col-start-auto sm:row-start-auto sm:col-span-3 text-right font-mono-tab ${(it.change_pct || 0) >= 0 ? "gain" : "loss"}`}>{fmtPct(it.change_pct)}</div>
                <div className="col-span-2 sm:col-span-2 text-right">
                  <Button variant="ghost" size="sm" onClick={(e) => remove(e, it.symbol)} data-testid={`watch-remove-${it.symbol}`}
                          className="rounded-full">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : (
        <Card className="p-16 text-center border-border rounded-xl">
          <div className="text-sm text-muted-foreground">Nothing here yet — type a ticker above, or tap the star on any asset.</div>
        </Card>
      )}
    </div>
  );
}
